import { useEffect, useMemo } from "react";
import { useLazyGetRolesByOrganizationQuery } from "../../../redux/roles/roleAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import { formatUuidT } from "../../../flatbuffers/display";
import { isMasterRoleName } from "../../../utils/isMasterAdmin";

/** A role an API client can be given: its id and the text an operator reads. */
export interface ApiClientRoleOption {
  id: string;
  label: string;
}

/**
 * The roles list the user editor's role select reads (FormInputSelectRole):
 * GET /roles/ filtered to one organization, answered as FlatBuffers or JSON
 * and nested at any of the depths that endpoint has used.
 */
function rolesFromResponse(payload: unknown): any[] {
  const p = payload as any;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
}

/**
 * The first non-blank of display name and name, falling back to the id, the
 * same resolution FormInputSelectRole uses so a role reads the same way in
 * both pickers and an option is never blank.
 */
function roleLabel(role: any, id: string): string {
  const candidates = [role?.displayName, role?.display_name, role?.name];
  return candidates.map((value) => String(value ?? "").trim()).find(Boolean) || id;
}

/**
 * Roles of orgId an API client may hold. MASTER ADMIN is left out: LiftCloud
 * refuses it for an API client whoever assigns it.
 */
export function useApiClientRoles(orgId: string): {
  roles: ApiClientRoleOption[];
  isFetching: boolean;
} {
  const [trigger, { data, isFetching }] = useLazyGetRolesByOrganizationQuery();

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await GETJwtToken();
        if (!cancelled) trigger({ token, orgId });
      } catch {
        // No token, no roles: the role select stays empty, and the page's
        // own list request reports the failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, trigger]);

  const roles = useMemo(
    () =>
      rolesFromResponse(data)
        .filter((role) => !isMasterRoleName(role?.name))
        .map((role) => {
          const id = formatUuidT(role?.id ?? role?.roleId ?? role?.role_id ?? "");
          return { id, label: roleLabel(role, id) };
        })
        .filter((role) => role.id !== ""),
    [data],
  );

  return { roles: orgId ? roles : [], isFetching };
}
